import React, { useState, useEffect } from 'react';
import { Spin } from 'antd';
import { EyeOutlined, LoadingOutlined } from '@ant-design/icons';
import { isVisitorRedFlagged } from 'utils/visitorsHelper';
import { OpBanner } from 'components/customAntd/DLS/OpBanner/OpBanner';
import warningIcon from 'images/icons/warning.svg';
import { DocumentCheck, VisitorAgreement } from 'types/visitTypes';
import { OpTabs } from 'components/customAntd/DLS/OpTabs/OpTabs';
import RedFlagMatchModal from './RedFlagMatchModal';
import { OpDrawer } from 'components/customAntd/DLS/OpDrawer/OpDrawer';
import ProfileDrawerHeader from './header/ProfileDrawerHeader';
import ProfileTab from './tabs/ProfileTab';
import AgreementsTab from './tabs/AgreementsTab';
import { getRequest } from 'api/apiClient';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store/store';
import { Agreement } from 'types/agreementTypes';
import { DenyDetails } from 'types/denyTypes';
import { QrCodeScanLog } from 'types/qrCodeTypes';
import QrCodeTab from './tabs/QrCodeTab';
import { fetchRedFlags, setSelectedRedFlaggedVisitor } from 'store/slices/redFlagSlice';
import { Kiosk } from 'types/kioskTypes';
import { useTranslation } from 'react-i18next';
import { describeVisitorWorkflowConfig } from 'store/slices/visitorWorkflowsSlice';
import ActivityLogTab from './tabs/ActivityLogTab';

interface ProfileDrawerProps {
    open: boolean;
    onClose: () => void;
}

const ProfileDrawer: React.FC<ProfileDrawerProps> = ({ open, onClose }) => {
    const dispatch: AppDispatch = useDispatch();

    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const { selectedVisit, selectedVisitor } = useSelector((state: RootState) => state.visits);
    const redFlags = useSelector((state: RootState) => state.redFlags.redFlags);
    const { isAltaVideoInstalled, devicesByWorkflow } = useSelector((state: RootState) => state.altaVideo);

    const [connectedKiosks, setConnectedKiosks] = useState<Kiosk[]>([]);
    const [visitorPhotoUrl, setVisitorPhotoUrl] = useState<string | null>(null);
    const [extendedVisitorInfo, setExtendedVisitorInfo] = useState<DocumentCheck | null>(null); // Extended visitor info includes additional properties like "photo" (e.g., "3090.jpg") and "agreement" (e.g., "3090_agreement.pdf")
    const [attendantAgreements, setAttendantAgreements] = useState<Agreement[]>([]);
    const [kioskAgreements, setKioskAgreements] = useState<Agreement[]>([]);
    const [signedAgreements, setSignedAgreements] = useState<Agreement[]>([]);
    const [denyDetails, setDenyDeatils] = useState<DenyDetails | undefined>(undefined);
    const [qrCodeScanLogs, setQrCodeScanLogs] = useState<QrCodeScanLog[]>([]);

    const [activeKey, setActiveKey] = useState<string>('profile');
    const [isRedFlagMatchModalOpen, setIsRedFlagMatchModalOpen] = useState(false);
    const [loading, setLoading] = useState<boolean>(false);

    const { t } = useTranslation();

    // Reset the active tab to 'profile' when the drawer opens
    useEffect(() => {
        if (open) {
            setActiveKey('profile');
        }
    }, [open]);

    useEffect(() => {
        const fetchWorkflowConfig = async () => {
            if (!orgId || !selectedVisit?.site?.id) return;

            try {
                const locationWorkflow = await getRequest(`/orgs/${orgId}/sites/${selectedVisit.site.id}/workflows`);
                const locationVisitorTypeWorkflow = await getRequest(`/orgs/${orgId}/sites/${selectedVisit.site.id}/visitorType/${selectedVisit?.visitorType.id}`);

                const defaultSignInWorkflowId = locationWorkflow?.data?.[0]?.SignInWorkflowId;
                const visitorTypeSignInWorkflowId = locationVisitorTypeWorkflow?.data?.[0]?.workflow?.SignInWorkflowId;

                const workflowId = visitorTypeSignInWorkflowId !== -1 ? visitorTypeSignInWorkflowId : defaultSignInWorkflowId;

                if (workflowId) {
                    dispatch(describeVisitorWorkflowConfig({
                        orgId,
                        visitorWorkflowId: workflowId,
                    }));
                }
            } catch (error) {
                console.error('Failed to fetch SignInWorkflowId for describeVisitorWorkflowConfig', error);
            }
        };

        fetchWorkflowConfig();
    }, [dispatch, orgId, selectedVisit?.site?.id, selectedVisit?.visitorType.id]);

    // Fetch data
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                // Fetch all independent data in parallel
                const [
                    signedVisitorAgreements,
                    kiosksResponse,
                    locationDefaultWorkflow,
                    locationVisitorTypeWorkflow,
                    extendedVisitorInfo,
                    denyDetailsResponse,
                    qrCodeScanLog
                ] = await Promise.all([
                    getRequest(`/orgs/${orgId}/visitor/${selectedVisit?.id}/visitorAgreement`, {
                        visitorId: selectedVisitor?.id
                    }),
                    getRequest(`/orgs/${orgId}/kiosk`),
                    getRequest(`/orgs/${orgId}/sites/${selectedVisit?.site?.id}/workflows`),
                    getRequest(`/orgs/${orgId}/sites/${selectedVisit?.site.id}/visitorType/${selectedVisit?.visitorType.id}`),
                    getRequest(`/orgs/${orgId}/visitor/${selectedVisit?.id}/documentCheck`),
                    getRequest(`/orgs/${orgId}/visitor/${selectedVisit?.id}/denyEntry`),
                    getRequest(`/orgs/${orgId}/visitor/${selectedVisit?.id}/qrScanLog`, {
                        visitorId: selectedVisitor?.id
                    })
                ]);

                // Process signed agreements
                const signedAgreements = signedVisitorAgreements.data.map((signedAgreement: VisitorAgreement) => signedAgreement.document);
                setSignedAgreements(signedAgreements);

                // Process kiosks
                const allKiosks: Kiosk[] = kiosksResponse.data || [];
                const validKiosks = allKiosks.filter(
                    (kiosk) => kiosk.status.id === 1 && kiosk.appearance?.[0]?.badgePrinter === 1
                );
                let matchingKiosks = validKiosks.filter(kiosk => kiosk.site?.id === selectedVisit?.site.id);

                // Fetch additional kiosks in parallel
                const kioskSitePromises = validKiosks.map(async (kiosk) => {
                    try {
                        const siteResponse = await getRequest(`/orgs/${orgId}/kiosk/${kiosk.id}/sites`);
                        const siteData = siteResponse.data || [];
                        if (siteData.some((site: any) => site.id === selectedVisit?.site.id)) {
                            return kiosk;
                        }
                    } catch (error) {
                        console.error(`Error fetching sites for kiosk ${kiosk.id}:`, error);
                    }
                    return null;
                });

                const additionalKiosksResults = await Promise.all(kioskSitePromises);
                const additionalKiosks = additionalKiosksResults.filter((k): k is Kiosk => k !== null);

                const finalKiosks = [...new Map([...matchingKiosks, ...additionalKiosks].map(kiosk => [kiosk.id, kiosk])).values()];
                setConnectedKiosks(finalKiosks);

                // Process workflows and fetch agreements in parallel
                const defaultWorkflowData = locationDefaultWorkflow?.data?.[0];
                const visitorTypeWorkflowData = locationVisitorTypeWorkflow?.data?.[0]?.workflow;

                const attendantWorkflowId =
                    visitorTypeWorkflowData?.SignInWorkflowId !== -1
                        ? visitorTypeWorkflowData.SignInWorkflowId
                        : defaultWorkflowData?.SignInWorkflowId;

                const kioskWorkflowId =
                    visitorTypeWorkflowData?.KioskInWorkflowId !== -1
                        ? visitorTypeWorkflowData.KioskInWorkflowId
                        : defaultWorkflowData?.KioskInWorkflowId;

                const [attendantAgreementsRes, kioskAgreementsRes] = await Promise.all([
                    getRequest(`/orgs/${orgId}/visitorWorkflow/${attendantWorkflowId}/agreements`),
                    getRequest(`/orgs/${orgId}/visitorWorkflow/${kioskWorkflowId}/agreements`)
                ]);

                setAttendantAgreements(attendantAgreementsRes.data || []);
                setKioskAgreements(kioskAgreementsRes.data || []);

                // Process extended visitor info
                const filteredExtendedVisitorInfo = extendedVisitorInfo.data.find((visitor: DocumentCheck) => visitor.id === selectedVisitor?.id);
                setExtendedVisitorInfo(filteredExtendedVisitorInfo);

                // Process deny details
                const denyDetails = denyDetailsResponse?.data?.[0]?.DenyDetails || [];
                const visitorMatch = denyDetails.find((denyDetail: any) =>
                    denyDetail.visitor.some((visitor: any) => visitor.id === selectedVisitor?.id)
                );
                if (visitorMatch) {
                    setDenyDeatils(visitorMatch);
                }

                // Set QR code logs
                setQrCodeScanLogs(qrCodeScanLog.data);

                // Fetch red flags and photo in parallel (conditional)
                const additionalPromises = [];

                if (redFlags.length === 0) {
                    additionalPromises.push(dispatch(fetchRedFlags({ orgId })));
                }

                if (filteredExtendedVisitorInfo?.photo) {
                    additionalPromises.push(
                        getRequest(
                            `/orgs/${orgId}/visitor/${selectedVisit?.id}/visitors/${selectedVisitor?.id}/photo`,
                            {},
                            { responseType: 'blob' }
                        ).then(imageBlob => {
                            const visitorPhotoUrl = URL.createObjectURL(imageBlob);
                            setVisitorPhotoUrl(visitorPhotoUrl);
                        }).catch(error => {
                            console.error('Failed to fetch visitor photo:', error);
                        })
                    );
                }

                if (additionalPromises.length > 0) {
                    await Promise.all(additionalPromises);
                }

            } catch (error) {
                console.log("Failed to fetch data.");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
        // eslint-disable-next-line
    }, [orgId]);

    const tabItems = [
        {
            key: 'profile',
            label: t('visitors.profile.tabs.profile'),
            children: <ProfileTab visitorPhotoUrl={visitorPhotoUrl} denyDetails={denyDetails} connectedKiosks={connectedKiosks} />
        },
        {
            key: 'agreements',
            label: t('visitors.profile.tabs.agreements'),
            children: <AgreementsTab extendedVisitorInfo={extendedVisitorInfo!} attendantAgreements={attendantAgreements} kioskAgreements={kioskAgreements} signedAgreements={signedAgreements} />,
        },
        {
            key: 'qrCodeScanLog',
            label: t('visitors.profile.tabs.qrCode'),
            children: <QrCodeTab qrCodeScanLogs={qrCodeScanLogs} />,
        },
        ...(isAltaVideoInstalled && devicesByWorkflow ? [{
            key: 'activityLog',
            label: t('visitors.profile.tabs.videoLog'),
            children: <ActivityLogTab />,
        }] : []),
    ];

    return (
        <>
            <OpDrawer
                width={700}
                title={t('visitors.profile.title')}
                placement="right"
                onClose={onClose}
                open={open}
                extra={!loading && (<ProfileDrawerHeader onClose={onClose} setDrawerLoading={setLoading} />)}
            >
                {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                    </div>
                ) : (
                    <>
                        {selectedVisitor && isVisitorRedFlagged(selectedVisitor, redFlags) && (
                            <>
                                <OpBanner
                                    type="error"
                                    style={{ marginBottom: '20px', cursor: 'pointer' }}
                                    showIcon={false}
                                    message={(
                                        <div onClick={() => {
                                            dispatch(setSelectedRedFlaggedVisitor(selectedVisitor));
                                            setIsRedFlagMatchModalOpen(true)
                                        }}
                                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <span style={{ display: 'flex', alignItems: 'center' }}>
                                                <img src={warningIcon} alt="Warning" style={{ width: '20px', height: '20px', marginRight: '8px' }} />
                                                {t('visitors.profile.banner.redFlag')}
                                            </span>
                                            <EyeOutlined />
                                        </div>
                                    )}
                                />
                                {(isRedFlagMatchModalOpen) && (
                                    <RedFlagMatchModal visible={isRedFlagMatchModalOpen} onClose={() => setIsRedFlagMatchModalOpen(false)} />
                                )}
                            </>
                        )}
                        <OpTabs activeKey={activeKey} onChange={setActiveKey} items={tabItems} />
                    </>
                )}
            </OpDrawer>
        </>
    );
};

export default ProfileDrawer;